import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { IoClose, IoDocumentTextOutline, IoOpenOutline, IoArrowForward } from 'react-icons/io5';
import { useTranslation } from 'react-i18next';
import { blogPosts } from '../content/blogPosts';

// Split a text string into React nodes, converting inline Markdown-ish
// markers:  [label](url) -> <a>, and **text** -> <strong>.
// Used for paragraph bodies and list items so the author-facing content
// in blogPosts.js can remain plain strings.
const renderInline = (text, isDarkMode, keyPrefix = '') => {
  if (!text) return null;
  const nodes = [];
  // Handle links first, then bold in the remaining pieces.
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIndex = 0;
  let match;
  let idx = 0;

  const pushWithBold = (plain, baseKey) => {
    if (!plain) return;
    const boldRegex = /\*\*([^*]+)\*\*/g;
    let bLast = 0;
    let bMatch;
    let bIdx = 0;
    while ((bMatch = boldRegex.exec(plain)) !== null) {
      if (bMatch.index > bLast) {
        nodes.push(plain.slice(bLast, bMatch.index));
      }
      nodes.push(
        <strong key={`${baseKey}-b-${bIdx++}`} className={isDarkMode ? 'text-white' : 'text-slate-900'}>
          {bMatch[1]}
        </strong>
      );
      bLast = boldRegex.lastIndex;
    }
    if (bLast < plain.length) {
      nodes.push(plain.slice(bLast));
    }
  };

  while ((match = linkRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      pushWithBold(text.slice(lastIndex, match.index), `${keyPrefix}-t-${idx}`);
    }
    nodes.push(
      <a
        key={`${keyPrefix}-a-${idx++}`}
        href={match[2]}
        target="_blank"
        rel="noopener noreferrer"
        className={`font-medium underline-offset-4 hover:underline ${
          isDarkMode ? 'text-sky-300 hover:text-sky-200' : 'text-sky-600 hover:text-sky-700'
        }`}
      >
        {match[1]}
      </a>
    );
    lastIndex = linkRegex.lastIndex;
  }
  if (lastIndex < text.length) {
    pushWithBold(text.slice(lastIndex), `${keyPrefix}-t-end`);
  }
  return nodes;
};

const BlogModal = ({ post, onClose, isDarkMode }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = originalOverflow;
    };
  }, [onClose]);

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-3 backdrop-blur-sm md:p-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 12, scale: 0.98 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        onClick={(e) => e.stopPropagation()}
        className={`relative flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-[26px] shadow-2xl ring-1 ${
          isDarkMode
            ? 'bg-[#0b1320] ring-white/10'
            : 'bg-white ring-slate-200/80'
        }`}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className={`absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full ring-1 transition ${
            isDarkMode
              ? 'bg-white/5 text-slate-200 ring-white/10 hover:bg-white/10'
              : 'bg-slate-100 text-slate-700 ring-slate-200 hover:bg-slate-200'
          }`}
        >
          <IoClose size={18} />
        </button>

        <div className="homepage-scrollbar flex-1 overflow-y-auto px-6 pb-8 pt-8 md:px-10 md:pt-10">
          <p className={`text-[10px] font-semibold uppercase tracking-[0.28em] ${
            isDarkMode ? 'text-sky-300/80' : 'text-sky-600/80'
          }`}>
            {post.category}
          </p>
          <h1 className={`mt-3 font-serif text-[1.6rem] font-semibold leading-tight md:text-[2rem] ${
            isDarkMode ? 'text-white' : 'text-slate-900'
          }`}>
            {post.title}
          </h1>
          <div className={`mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] ${
            isDarkMode ? 'text-slate-400' : 'text-slate-500'
          }`}>
            <span>{post.author}</span>
            <span aria-hidden>•</span>
            <span>{post.date}</span>
            <span aria-hidden>•</span>
            <span>{post.readingTime}</span>
          </div>

          {post.primaryLink && (
            <div className={`mt-5 rounded-2xl border px-4 py-3 text-sm ${
              isDarkMode
                ? 'border-sky-400/20 bg-sky-400/10 text-sky-100'
                : 'border-sky-200 bg-sky-50 text-sky-800'
            }`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span className="font-medium">
                  {post.primaryLinkLabel || 'Read more on JournalMetrics.org'}
                </span>
                <a
                  href={post.primaryLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                    isDarkMode
                      ? 'bg-sky-400/20 text-sky-100 hover:bg-sky-400/30'
                      : 'bg-white text-sky-700 hover:bg-sky-100'
                  }`}
                >
                  Visit site <IoOpenOutline size={14} />
                </a>
              </div>
            </div>
          )}

          <article className={`mt-7 space-y-5 text-[15.5px] leading-[1.75] ${
            isDarkMode ? 'text-slate-200' : 'text-slate-700'
          }`}>
            {post.sections.map((block, i) => {
              if (block.type === 'heading') {
                return (
                  <h2
                    key={i}
                    className={`pt-3 font-serif text-[1.2rem] font-semibold tracking-tight md:text-[1.3rem] ${
                      isDarkMode ? 'text-white' : 'text-slate-900'
                    }`}
                  >
                    {block.text}
                  </h2>
                );
              }
              if (block.type === 'paragraph') {
                return (
                  <p key={i}>
                    {renderInline(block.text, isDarkMode, `p${i}`)}
                  </p>
                );
              }
              if (block.type === 'list') {
                const ListTag = block.ordered ? 'ol' : 'ul';
                const listClass = block.ordered
                  ? 'list-decimal pl-6 space-y-2'
                  : 'list-disc pl-6 space-y-2';
                return (
                  <ListTag key={i} className={listClass}>
                    {block.items.map((item, j) => (
                      <li key={j}>{renderInline(item, isDarkMode, `l${i}-${j}`)}</li>
                    ))}
                  </ListTag>
                );
              }
              return null;
            })}
          </article>

          {post.primaryLink && (
            <div className="mt-8 flex justify-center">
              <a
                href={post.primaryLink}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition ${
                  isDarkMode
                    ? 'bg-sky-500/90 text-white hover:bg-sky-400'
                    : 'bg-sky-600 text-white hover:bg-sky-700'
                }`}
              >
                Explore journal metrics <IoArrowForward size={16} />
              </a>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

const BlogSection = ({ isDarkMode }) => {
  const { t } = useTranslation();
  const [openPostId, setOpenPostId] = useState(null);
  const featured = blogPosts[0];

  if (!featured) return null;

  const openPost = blogPosts.find((p) => p.id === openPostId);

  return (
    <section
      className={`rounded-[28px] border p-4 ${
        isDarkMode ? 'border-white/8 bg-[#07111f]/82' : 'border-slate-200/90 bg-white/88'
      }`}
    >
      <div className="flex items-center justify-between gap-4">
        <div>
          <p
            className={`text-[10px] font-semibold uppercase tracking-[0.28em] ${
              isDarkMode ? 'text-sky-300/70' : 'text-sky-600/80'
            }`}
          >
            {t('homeTab.blogKicker', 'From the blog')}
          </p>
          <h3
            className={`mt-1.5 text-[1.08rem] font-semibold ${
              isDarkMode ? 'text-white' : 'text-slate-900'
            }`}
          >
            {t('homeTab.blogTitle', 'Research reading room')}
          </h3>
        </div>
        <div
          className={`rounded-full p-2 ${
            isDarkMode ? 'bg-sky-400/10 text-sky-200' : 'bg-sky-50 text-sky-600'
          }`}
        >
          <IoDocumentTextOutline size={16} />
        </div>
      </div>

      <motion.button
        onClick={() => setOpenPostId(featured.id)}
        whileHover={{ y: -1 }}
        whileTap={{ scale: 0.99 }}
        className={`mt-4 flex w-full flex-col items-start gap-2 rounded-2xl border px-4 py-3.5 text-left transition-shadow ${
          isDarkMode
            ? 'border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:shadow-[0_8px_24px_rgba(0,0,0,0.28)]'
            : 'border-slate-200 bg-white/70 hover:bg-white hover:shadow-[0_8px_24px_rgba(15,23,42,0.08)]'
        }`}
      >
        <div className="flex w-full items-center justify-between gap-3">
          <span
            className={`text-[10px] font-semibold uppercase tracking-[0.22em] ${
              isDarkMode ? 'text-slate-400' : 'text-slate-500'
            }`}
          >
            {featured.category}
          </span>
          <span
            className={`text-[11px] ${
              isDarkMode ? 'text-slate-500' : 'text-slate-400'
            }`}
          >
            {featured.readingTime}
          </span>
        </div>
        <h4
          className={`text-[15px] font-semibold leading-snug ${
            isDarkMode ? 'text-white' : 'text-slate-900'
          }`}
        >
          {featured.title}
        </h4>
        <p
          className={`text-[13px] leading-relaxed ${
            isDarkMode ? 'text-slate-300/90' : 'text-slate-600'
          }`}
        >
          {featured.excerpt}
        </p>
        <span
          className={`mt-1 inline-flex items-center gap-1 text-[12px] font-semibold ${
            isDarkMode ? 'text-sky-300' : 'text-sky-600'
          }`}
        >
          {t('homeTab.blogReadMore', 'Read the guide')} <IoArrowForward size={12} />
        </span>
      </motion.button>

      <AnimatePresence>
        {openPost && (
          <BlogModal
            post={openPost}
            isDarkMode={isDarkMode}
            onClose={() => setOpenPostId(null)}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

export default BlogSection;
